import React from "react";
import ReactDOM from "react-dom";
import Employee from "../../../app/domain/Employee";
import {
  findCompanyId,
  findCompanyDetail,
} from "../../../infrastructure/repository/company-repository";
import { findEmployeesWithFields } from "../../../infrastructure/employees-cache";
import { findEmployeeFilter } from "../../../infrastructure/employees-filter";
import { findModels } from "../../../infrastructure/repository/benefit-models-repository";
import { logError, addContextLog } from "../../../infrastructure/log";
import { translate } from "../../../infrastructure/translate/localization";
import { shouldFilterTaxRegimes } from "../../../infrastructure/country-rules";
import { findSelectedEmployees } from "../select-employees";
import CategoryEmployeeTable from "../components/CategoryEmployeesTable";
import { showErrorMessage } from "../../commons/show-error-message";

export class UploadingEmployee {
  constructor({
    id,
    internalId,
    name,
    surnames,
    benefitModelName,
    email,
    error,
    warning,
    legalId,
  }) {
    this.id = id;
    this.internalId = internalId;
    this.name = name;
    this.surnames = surnames;
    this.legalId = legalId;
    this.benefitModelName = benefitModelName;
    this.email = email;
    this.error = error;
    this.warning = warning;
  }
}

export function renderCategoryEmployees({
  category,
  employees,
  employeeIds,
  selectedIds,
  models,
  currentPage,
  pageCount,
  showTable,
  taxRegimes,
}) {
  const results = employees.map(
    (e) =>
      new UploadingEmployee({
        id: e.id,
        internalId: e.internalId || "-",
        name: e.name,
        surnames: e.surname,
        legalId: e.legalId,
        benefitModelName: models.find((m) => m.id === e.modelId).name,
        email: e.email,
        error: e.error,
        warning: e.warning,
      })
  );

  const target = document.getElementById(
    `table-${category}-employees-container`
  );

  if (target) {
    ReactDOM.render(
      <CategoryEmployeeTable
        category={category}
        uploadingEmployees={results}
        currentPage={currentPage}
        employeeIds={employeeIds}
        selected={selectedIds}
        pageCount={pageCount}
        showTable={showTable}
        taxRegimes={taxRegimes}
        models={models}
      />,
      target
    );
  }
}

export async function displayCategoryEmployees({
  category,
  pageSize = 15,
  currentPage = 0,
} = {}) {
  try {
    const keyToFilter = ["internalId", "name", "surname", "legalId", "email"];

    const companyId = await findCompanyId();
    const companyDetail = await findCompanyDetail();
    const allEmployees = await findEmployeesWithFields({ companyId });
    const notRemovedEmployees = allEmployees.filter(
      (e) => e.state !== "removed"
    );

    let showTable = false;
    let employees;

    switch (category) {
      case "uploading":
        employees = notRemovedEmployees.filter(
          (e) => e.state === "uploading" && !e.error && !e.warning
        );
        break;
      case "warning":
        employees = notRemovedEmployees.filter(
          (e) => e.state === "uploading" && !e.error && e.warning
        );
        break;
      case "error":
        employees = notRemovedEmployees.filter(
          (e) => e.state === "uploading" && e.error
        );
        break;

      default:
        break;
    }

    if (employees.length > 0) {
      showTable = true;
    }

    const currentEmployees = employees.filter(
      findEmployeeFilter({ forState: category, keys: keyToFilter })
    );

    const employeeCount = currentEmployees.length;
    const pageCount = Math.ceil(employeeCount / pageSize);

    let pageIndex = currentPage;

    if (pageIndex > pageCount - 1) pageIndex = pageCount - 1;
    if (pageIndex < 0) pageIndex = 0;

    const employeesInPage = currentEmployees.slice(
      pageIndex * pageSize,
      (pageIndex + 1) * pageSize
    );
    const { data: models } = await findModels();

    const employeeIds = currentEmployees.map((e) => e.id);
    const selectedIds = findSelectedEmployees({ of: employeeIds });

    addContextLog("info", "displayCategoryEmployees", "display-screen", {
      companyId,
      category,
      employeeIds,
    });

    renderCategoryEmployees({
      category,
      employees: employeesInPage,
      employeeIds,
      selectedIds,
      models,
      currentPage: pageIndex,
      pageCount,
      showTable,
      taxRegimes: shouldFilterTaxRegimes(companyDetail.data.countryISO2Code)
        ? [Employee.taxRegimes[0]]
        : Employee.taxRegimes,
    });
  } catch (e) {
    logError(e, `Error to displayCategoryEmployees category ${category}`);
    showErrorMessage(translate("common-messages.basic-error-message"), true);
  }
}
